import { integrationsActions } from "../../Everou/_actions/integrations.actions";
import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import { resolveLocationOrganizationUid } from "../_helpers/activeOrganization";
import { PMSActiveOrganizationStore } from "../_stores/PMSActiveOrganizationStore";
import { PMSOrganizationTabStore } from "../_stores/PMSOrganizationTabStore";
import { PMSFocusedTemplateStore } from "../_stores/PMSFocusedTemplateStore";
import { PMSAccountDraftStore } from "../_stores/PMSAccountDraftStore";
import { IntegrationAccountDraft } from "../../Everou/_modelsTS/Integration";

// The account an integration hangs from, as the integration's row knows it.
export type IntegrationTemplateRef = {
    uid: string;
    name?: string | null;
};

export const PMSNavigationActions = {
    goToIntegrationTemplate,
    openIntegrationCompany,
};

// Open the company's panel of the accounts module, narrowed to `template`
// when the caller comes from a concrete integration: "edit the integration"
// on a location used to land on the company's whole list -- which, for a
// location outside any organization, is the VIEWER's personal accounts,
// never the colleague's account the integration actually hangs from.
// With `draft` instead (an orphan integration's credentials), the panel
// opens on a new account prefilled from them: the row has no account to
// narrow to, but its credentials are the ones the account needs (BAC-672).
function openIntegrationCompany(
    company: string,
    template: IntegrationTemplateRef | null = null,
    draft: IntegrationAccountDraft | null = null,
) {
    return (dispatch) => {
        dispatch(PMSFocusedTemplateStore.actionSet(
            template?.uid ? { company, uid: template.uid, name: template.name ?? null } : null,
        ));
        dispatch(PMSAccountDraftStore.actionSet(draft ? { company, ...draft } : null));
        dispatch(integrationsActions.changeSecondaryView(company));
    };
}

// Accounts of an organization live in its PMS accounts tab; the personal
// (legacy) ones in the standalone module. Same scope rule as the location's
// account listing (resolveLocationOrganizationUid). A location in the personal
// scope (outside any organization, or of one the user is not a member of)
// does not come through here when the user has organizations: its personal
// accounts are browsed in place (IntegrationConfig.browseAccount).
// PMSModulesActions/PMSModules are imported lazily to avoid a load-time
// circular import (PMSModules -> ... -> the components calling this ->
// PMSModules).
function goToIntegrationTemplate(
    company: string,
    template: IntegrationTemplateRef | null = null,
    draft: IntegrationAccountDraft | null = null,
) {
    return async (dispatch, getState) => {
        const organizationUid = resolveLocationOrganizationUid(getState());

        dispatch(openIntegrationCompany(company, template, draft));
        const [{ PMSModulesActions }, { MODULE_IDS }] = await Promise.all([
            import("../PMSModulesActions"),
            import("../PMSModules"),
        ]);

        if (!organizationUid) {
            dispatch(PMSModulesActions.selectModule(MODULE_IDS.INTEGRATIONS));
            return;
        }
        dispatch(OrganizationsActions.selectOrganization(organizationUid));
        dispatch(PMSActiveOrganizationStore.actionSet(organizationUid));
        dispatch(PMSOrganizationTabStore.actionSet("INTEGRATIONS"));
        dispatch(PMSModulesActions.selectModule(MODULE_IDS.ORGANIZATIONS));
    };
}
